import { resolveThreshold, useKpis } from '../kpis'
import { QueryResult, ViewConfig } from '../view-config'
import { MetricView } from './MetricView'
import { RankedListView } from './RankedListView'
import { ReportChart, ReportChartType } from './ReportChart'
import { ReportTable } from './ReportTable'
import { TableCard } from './TableCard'
import { ReportView, ViewSwitcher } from './ViewSwitcher'

/**
 * The shared report preview: a "Show as" switcher above a live, shape-gated
 * rendering of a ViewConfig + its QueryResult. Used by both the flat report
 * editor (with a sidebar) and the pipeline builder (with an op-chain editor), so
 * a report reads identically however it was built. The caller owns `view` and
 * how a view change is persisted.
 *
 * @param config - the report config to render
 * @param result - the computed query result for `config`
 * @param view - the active presentation view (clamped to the shape by the caller)
 * @param onViewChange - invoked when the user picks a different view
 */
export function ReportViewer({
  config: rawConfig,
  result,
  view,
  onViewChange,
}: {
  config: ViewConfig
  result: QueryResult
  view: ReportView
  onViewChange: (view: ReportView) => void
}) {
  // Swap any KPI-linked target for its live value before rendering.
  const { kpis } = useKpis()
  const config = resolveThreshold(rawConfig, kpis)
  return (
    <TableCard
      toolbar={
        <ViewSwitcher
          config={config}
          result={result}
          view={view}
          onChange={onViewChange}
        />
      }
    >
      {view === 'metric' ? (
        <div className="p-5">
          <div className="max-w-sm">
            <MetricView config={config} result={result} />
          </div>
        </div>
      ) : view === 'table' ? (
        <ReportTable config={config} result={result} />
      ) : view === 'ranked' ? (
        <div className="p-4">
          <RankedListView config={config} result={result} />
        </div>
      ) : (
        <ReportChart
          bare
          chartType={view as ReportChartType}
          config={config}
          result={result}
        />
      )}
    </TableCard>
  )
}
